import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useCallback, useEffect, useState } from "react"

import { type EditorConfig, useEditorConfig } from "../../../EditorConfig"
import {
  assetApiHeaders,
  type MediaAsset,
  mediaAssetType,
} from "../hooks/mediaLibraryData"

export type UploadFile = NewUploadFile | UploadedFile

interface NewUploadFile {
  id: string
  file: File
  status: "idle" | "uploading" | "error"
}

interface UploadedFile {
  id: string
  file: File
  status: "success"
  response: MediaAsset
}

interface UseMediaLibraryUploadArgs {
  /**
   * The upload is about to begin.
   */
  onBeforeUpload: (files: UploadFile[]) => void
  /**
   * The upload has completed.
   */
  onComplete?: (files: UploadFile[]) => void
}

/**
 * Uploads files to the media library.
 *
 * @description Fork of mediaPickerData.ts
 */
export function useMediaLibraryUpload(args: UseMediaLibraryUploadArgs) {
  const { onBeforeUpload, onComplete } = args
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([])

  const isUploading = uploadingFiles.some(
    (file) => file.status === "idle" || file.status === "uploading",
  )

  const config = useEditorConfig()

  function uploadNextFile() {
    // Nothing to do.
    if (!uploadingFiles.length) return

    // Already uploading a file. Wait for it to finish.
    if (uploadingFiles.some((file) => file.status === "uploading")) return

    // All the files are already in error, nothing to do on behalf of the user.
    if (uploadingFiles.every((file) => file.status === "error")) return

    const nextFile = uploadingFiles.find((file) => file.status === "idle")

    // We have some selected files but everything was already uploaded. Can happen while we show the final state for a bit.
    if (!nextFile) return

    // Since we have to update this async state a few times within the scope of a single function,
    // keep a local working copy.
    let currentFiles = uploadingFiles

    function updateCurrentFiles(update: (files: UploadFile[]) => UploadFile[]) {
      currentFiles = update(currentFiles)
      setUploadingFiles(currentFiles)

      const isComplete = currentFiles.every(
        (file) => file.status === "success" || file.status === "error",
      )

      if (!isComplete) return

      // Pause a bit so users can see the final state even on very fast connections.
      setTimeout(() => {
        setUploadingFiles([])
        onComplete?.(currentFiles)
      }, 1_500)
    }

    updateCurrentFiles((files) =>
      files.map((file) =>
        file.id === nextFile.id ? { ...file, status: "uploading" } : file,
      ),
    )

    uploadMedia({
      file: nextFile.file,
      config,
    })
      .then((responseFile) => {
        updateCurrentFiles((files) =>
          files.map((file) =>
            file.id === nextFile.id
              ? { ...file, status: "success", response: responseFile }
              : file,
          ),
        )
      })
      .catch(() => {
        updateCurrentFiles((files) =>
          files.map((file) =>
            file.id === nextFile.id ? { ...file, status: "error" } : file,
          ),
        )
      })
  }

  function onFilesSelected(files: File[]) {
    const newUploadingFiles: UploadFile[] = files.map((file) => ({
      id: `temp-${crypto.randomUUID()}`,
      file,
      status: "idle",
    }))

    setUploadingFiles(newUploadingFiles)
    onBeforeUpload(newUploadingFiles)
  }

  const onRetry = useCallback(
    (failedFile: UploadFile) => {
      const newFiles = [
        { ...failedFile, status: "idle" as const },
        ...uploadingFiles.filter((file) => file.id !== failedFile.id),
      ]
      setUploadingFiles(newFiles)
      onBeforeUpload(newFiles)
    },
    [onBeforeUpload, uploadingFiles],
  )

  useEffect(uploadNextFile, [config, uploadingFiles, onComplete])

  return {
    isUploading,
    uploadingFiles,
    onFilesSelected,
    onRetry,
  }
}

export async function uploadMedia(params: {
  file: File
  config: EditorConfig
}) {
  const { file, config } = params
  if (!config.mediaLibrary) throw new Error("Media Library not configured.")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  const body = new FormData()
  body.append(file.name, file)

  return request(new URL("assets", assetsApiBaseUrl), {
    headers: {
      repository,
      "Content-Type": "multipart/form-data",
      ...assetApiHeaders,
    },
    method: "POST",
    body,
    credentials: getRequestCredentials(authStrategy),
    okType: mediaAssetType,
  })
}
